import { useQuery } from "react-query";
import ApiValidate from "../../common/components/ApiValidate";
import usePermissions from "../../common/hooks/pages/usePermissions";
import { usePopup } from "../../common/contexts/PopupContext";
import { useApp } from "../../common/contexts/AppContext";
import Button from "../../common/components/Button";
import { Info } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { DisplayPermissions } from "../../profile/components/PagePermissions";

function PagePermissionsForm({ pagePermissions, setPagePermissions, error }) {
  const { getPermissions } = usePermissions();

  const { pages } = useApp();

  const { toast } = usePopup();

  const { data, isLoading, isError } = useQuery({
    retry: false,
    queryKey: "permissions",
    queryFn: getPermissions,
    onSuccess: () => {},
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        "Something went wrong while getting permissions";
      toast(message, "error");
    },
  });

  const { permissions = [] } = data?.data || {};

  return (
    <div>
      <div className="flex items-center justify-between mt-8 mb-6">
        <div className="flex items-center space-x-3">
          <div className=" text-lg">Page permissions</div>
          <div id="permissions-info">
            <Info size={20} />
          </div>

          <Tooltip anchorSelect="#permissions-info">
            <DisplayPermissions permissions={permissions} />
          </Tooltip>
        </div>

        <Button
          buttonProps={{
            className: "back_link",
            onClick: () => setPagePermissions({}),
          }}
        >
          Unselect all
        </Button>
      </div>

      <ApiValidate isLoading={isLoading} isError={isError}>
        {pages?.map((page, i) => {
          const pageId = page?._id;

          return (
            <PageForm
              page={page}
              permissions={permissions}
              key={i}
              isPageSelected={pagePermissions[pageId] || false}
              onPageSelect={(isSelected) => {
                setPagePermissions((prev) => {
                  const prevPages = { ...prev };

                  if (isSelected) {
                    prevPages[pageId] = permissions?.map((perm) => perm?._id);
                  } else {
                    delete prevPages[pageId];
                  }

                  return prevPages;
                });
              }}
              onPermSelect={(isSelected, permissionId) => {
                setPagePermissions((prev) => {
                  const prevPages = { ...prev };

                  let prevPerms = prevPages[pageId] || [];

                  if (isSelected) {
                    prevPerms.push(permissionId);
                  } else {
                    prevPerms = prevPerms.filter((p) => p !== permissionId);
                  }

                  if (prevPerms?.length) {
                    prevPages[pageId] = prevPerms;
                  } else {
                    delete prevPages[pageId];
                  }

                  return prevPages;
                });
              }}
            />
          );
        })}
      </ApiValidate>

      <div className="my-2 text-red-600">{error}</div>
    </div>
  );
}

export default PagePermissionsForm;

function PageForm({
  page = {},
  permissions = [],
  isPageSelected,
  onPageSelect,
  onPermSelect,
}) {
  const { name } = page;

  return (
    <div>
      <div className="">
        <Checkbox
          label={name}
          onChange={(e) => onPageSelect(e.target.checked)}
          isChecked={isPageSelected}
        />
      </div>

      {isPageSelected ? (
        <div className="ml-4">
          {permissions.map((perm, i) => {
            const { label } = perm;

            const isPermissionSelected =
              isPageSelected && isPageSelected.includes(perm?._id);

            return (
              <div key={i} className="my-2">
                <Checkbox
                  label={label}
                  onChange={(e) => {
                    const isChecked = e.target.checked;

                    onPermSelect(isChecked, perm?._id);
                  }}
                  isChecked={isPermissionSelected}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Checkbox({ label, onChange, isChecked }) {
  return (
    <div>
      <label>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onChange}
          className="mr-2"
        />
        {label}
      </label>
    </div>
  );
}
